import React, { createContext, useContext } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Shield,
  AlertTriangle,
  ClipboardList,
  Circle,
  FileText,
  GraduationCap,
  BookOpen,
  ShieldCheck,
  Activity,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { queryLoadFailure } from '../../lib/load-failure';
import {
  CERT_REVIEWER_ROLES,
  pendingCertsQuery,
  safetyDashboardQuery,
  teamCertSummaryQuery,
} from './safety-queries';
import NewSafetyFormButton, { NEW_FORM_ROLES } from './NewSafetyFormButton';

/**
 * ── THE /safety FAMILY'S ONE BAND AND ONE STRIP ──────────────────────────────
 *
 * fe#757 put /safety on the band and turned its six header buttons into a tab
 * strip, and deferred this: every destination was its own top-level route with
 * its own header, so the strip did not survive the click. This is the layout
 * route that makes it survive. It owns the one `<h1>`, the band and the strip;
 * the six pages under it own neither (fe#744 — the Settings `SafetyWorkspace`,
 * an unrelated page with an unfortunate name, already works this way).
 *
 * A PATHLESS LAYOUT, SO NOTHING MOVED. Every child keeps the URL it had, which
 * matters most for `/cert-review`: it sits outside the `/safety` namespace, and
 * reviewer notifications deep-link to it with `?highlight=`. React Router lets
 * a pathless layout hold absolute children from any namespace, so the queue is
 * under the strip without a redirect or a new address.
 *
 * ONLY THE AppLayout TREE. A field worker reaches `/safety/my-training` through
 * their own tree, which has no `/safety` at all (their safety home is
 * `/field/safety`); a strip there would link them into routes their tree does
 * not register. `useInSafetyLayout()` is how a page that is ALSO registered
 * outside this layout knows whether to draw its own header.
 *
 * THE BAND'S STATS ARE THE SAME ON EVERY TAB, on purpose. They are the family's
 * headline figures, not the current page's, and they come from the three shared
 * reads in `safety-queries.ts` — the same cache entries /safety's panels and the
 * Cert Review queue read, so the band and the body can never show two answers.
 */

const SafetyLayoutContext = createContext(false);

/** True inside `SafetyLayout`. A page registered in more than one route tree
 *  draws its own header only when this is false. */
export function useInSafetyLayout(): boolean {
  return useContext(SafetyLayoutContext);
}

// ─── The section strip ───────────────────────────────────────────────────────
/**
 * TABS, NOT CHIPS: each one switches WHICH QUESTION the page answers (fe#704),
 * and a strip goes in the body directly under the band, never on it (fe#712).
 * Links with `aria-current="page"`, since each is its own route.
 *
 * NO COUNTS ON THE TABS — a count on a tab is a count of a different dataset.
 * Cert Review's BADGE is not a tally: it is an alert about work waiting for
 * this reader, the only one in the strip, drawn as a pill so it cannot read as
 * one of a comparable set.
 *
 * Custom Forms is a tab of Records, not a question of its own, and keeps its
 * own entry only because nothing else signposts it (fe#757's docblock). It is
 * the current tab when Records is showing its Custom Forms list, which Records
 * writes to the URL for exactly this reason.
 */
type SectionKey =
  | 'overview'
  | 'records'
  | 'custom-forms'
  | 'metrics'
  | 'training'
  | 'my-training'
  | 'cert-review';

type Section = { key: SectionKey; label: string; to: string; icon: React.ElementType };

const SECTIONS: Section[] = [
  { key: 'overview', label: 'Overview', to: '/safety', icon: Shield },
  { key: 'records', label: 'Records', to: '/safety/records', icon: FileText },
  { key: 'custom-forms', label: 'Custom Forms', to: '/safety/records?tab=custom_forms', icon: ClipboardList },
  { key: 'metrics', label: 'Metrics', to: '/safety/metrics', icon: Activity },
  { key: 'training', label: 'Compliance Training', to: '/safety/training', icon: GraduationCap },
  { key: 'my-training', label: 'My Training', to: '/safety/my-training', icon: BookOpen },
];

/** Which tab the current location is. Exact paths only: a child route this
 *  layout does not hold marks nothing, rather than guessing a parent. */
export function activeSection(pathname: string, search: string): SectionKey | null {
  const path = pathname.replace(/\/+$/, '') || '/';
  switch (path) {
    case '/safety':
      return 'overview';
    case '/safety/records':
      return new URLSearchParams(search).get('tab') === 'custom_forms' ? 'custom-forms' : 'records';
    case '/safety/metrics':
      return 'metrics';
    case '/safety/training':
      return 'training';
    case '/safety/my-training':
      return 'my-training';
    case '/cert-review':
      return 'cert-review';
    default:
      return null;
  }
}

function SectionStrip({
  active,
  isReviewer,
  pendingCertCount,
}: {
  active: SectionKey | null;
  isReviewer: boolean;
  pendingCertCount: number | undefined;
}) {
  const item = (on: boolean) =>
    `relative flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap px-3 py-2 text-sm transition-colors ${
      on
        ? 'font-extrabold text-fieldiq-text'
        : 'font-semibold text-fieldiq-muted hover:text-fieldiq-text'
    }`;

  /* The amber rule under the word, plus the weight, carries the active state;
   * the word itself stays `--fieldiq-text`. `--fieldiq-accent-signal` is 4.36
   * on `--fieldiq-dark` in light mode, under the floor on the one surface a
   * body strip sits on. The text-decoration utility's name is deliberately not
   * written here — Tailwind's scanner reads comments as source. */
  const rule = <span aria-hidden="true" className="absolute bottom-0 left-0 right-0 h-[2px] bg-fi-amber" />;

  return (
    <div
      data-testid="safety-section-strip"
      role="navigation"
      aria-label="Safety sections"
      className="scrollbar-hide flex gap-1 overflow-x-auto border-b border-fieldiq-border"
    >
      {SECTIONS.map((s) => {
        const Icon = s.icon;
        const on = s.key === active;
        return (
          <Link key={s.key} to={s.to} aria-current={on ? 'page' : undefined} className={item(on)}>
            <Icon size={14} aria-hidden="true" />
            {s.label}
            {on && rule}
          </Link>
        );
      })}

      {isReviewer && (
        <Link
          to="/cert-review"
          aria-current={active === 'cert-review' ? 'page' : undefined}
          className={item(active === 'cert-review')}
        >
          <ShieldCheck size={14} aria-hidden="true" />
          Cert Review
          {/* Absent, not zero: the pending query can fail or be refused, and
            * "no certs waiting" is the reassuring sentence this page's whole
            * family of bugs is about. No badge at all when the figure did not
            * arrive — a dash inside an alert pill would read as a count. */}
          {pendingCertCount !== undefined && pendingCertCount > 0 && (
            <span data-testid="cert-review-badge" className="ml-0.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-fi-pill bg-fi-signal-red px-1 text-[10px] font-bold leading-none text-fi-signal-ink">
              {pendingCertCount > 99 ? '99+' : pendingCertCount}
            </span>
          )}
          {active === 'cert-review' && rule}
        </Link>
      )}
    </div>
  );
}

// ─── The layout ──────────────────────────────────────────────────────────────
export default function SafetyLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isReviewer = CERT_REVIEWER_ROLES.includes(user?.role);

  const dashboardQuery = useQuery(safetyDashboardQuery);
  const teamCertsQuery = useQuery(teamCertSummaryQuery);
  const pendingQuery = useQuery({ ...pendingCertsQuery, enabled: isReviewer });

  const dashboardFailure = queryLoadFailure(dashboardQuery);
  const teamCertsFailure = queryLoadFailure(teamCertsQuery);
  const pendingFailure = queryLoadFailure(pendingQuery);

  const dashboard: any = dashboardQuery.data;
  const teamCerts: any = teamCertsQuery.data;

  /* HAS IT ARRIVED, and IS IT STILL IN FLIGHT — two questions, moved here from
   * /safety with the band (fe#758). `!arrived` is true while in flight AND
   * forever after a failure, so it cannot drive the pulse: a failed read has
   * arrived at an answer ("we could not read this"), spelled as the em dash
   * the value already resolves to. `pending` is the in-flight half only. One
   * failed call dashes its own stats and nobody else's. */
  const dashboardArrived = !dashboardQuery.isLoading && !dashboardFailure && dashboard !== undefined;
  const teamCertsArrived = !teamCertsQuery.isLoading && !teamCertsFailure && teamCerts !== undefined;
  const dashboardPending = !dashboardArrived && !dashboardFailure;
  const teamCertsPending = !teamCertsArrived && !teamCertsFailure;

  /* "SINCE LAST INCIDENT" HAS THREE STATES. A number; a dash with a sub-line
   * naming which kind of nothing it is (an EMPTY SET — no incident ever — is
   * the best news on the page, and not the same as a figure that did not
   * arrive, fe#702 / api#700); and a failed read, which carries no sub-line
   * at all because it knows nothing about this company's incidents. */
  const lastIncident = dashboard?.lastIncident ?? null;
  const daysSince = dashboard?.daysSinceLastIncident;
  const hasDaysSince = typeof daysSince === 'number' && Number.isFinite(daysSince);
  const daysSinceSub = !dashboardArrived
    ? undefined
    : hasDaysSince
      ? 'Days, recordable incidents'
      : lastIncident
        ? 'Incident date not recorded'
        : 'No incident on record';

  /* EXPIRED ALONE, never summed with expiring (fe#757): an expired
   * certification means that person cannot be sent out today; expiring-soon is
   * a planning number and keeps its own row on /safety. */
  const certsExpired = teamCerts?.expired;
  const pendingCertCount = pendingFailure ? undefined : pendingQuery.data?.length;
  const section = activeSection(location.pathname, location.search);
  /* "New form" belongs to the Custom Forms tab only: it starts a custom form,
   * and on any other tab it would read as starting one of that tab's records. */
  const canStartForm = section === 'custom-forms' && NEW_FORM_ROLES.includes(user?.role);

  return (
    <SafetyLayoutContext.Provider value={true}>
      <div data-testid="safety-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
        {/* The app shell renders the one global <Header />. Pages never do. */}
        <PageHeaderBand
          eyebrow="Health, safety & compliance"
          title="Safety & Compliance"
          onBack={() => navigate(-1)}
          backLabel="Back"
          actions={canStartForm ? <NewSafetyFormButton /> : undefined}
          /* FOUR STATS, AND THE THIRD LABEL IS SEVEN CHARACTERS ON PURPOSE. The
             hero circle's edge crosses the third stat column between 1024 and
             1048 wide and the labels swept there run to about 70px; "Expired"
             sits well inside that. Unchanged from fe#757, and the band carries
             no description, so its measured clearance still holds. */
          stats={[
            {
              icon: <Shield className="size-4" aria-hidden="true" />,
              label: 'Since Last Incident',
              sub: daysSinceSub,
              tone: 'success',
              loading: dashboardPending,
              value: hasDaysSince ? daysSince : '—',
            },
            {
              icon: <ClipboardList className="size-4" aria-hidden="true" />,
              label: 'JSAs Awaiting',
              sub: 'Submitted, not approved',
              tone: 'info',
              loading: dashboardPending,
              value: displayValue(dashboard?.pendingJsaApprovals),
            },
            {
              icon: <AlertTriangle className="size-4" aria-hidden="true" />,
              label: 'Expired',
              sub: 'Certifications',
              tone: 'accent',
              loading: teamCertsPending,
              value: displayValue(certsExpired),
            },
            {
              icon: <Circle className="size-4" aria-hidden="true" />,
              label: 'Near Misses',
              sub: 'Reported, all time',
              tone: 'neutral',
              loading: dashboardPending,
              value: displayValue(dashboard?.nearMissCount),
            },
          ]}
        />

        <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
          <div className="mx-auto max-w-[1920px] space-y-4">
            <SectionStrip
              active={section}
              isReviewer={isReviewer}
              pendingCertCount={pendingCertCount}
            />
            <Outlet />
          </div>
        </section>
      </div>
    </SafetyLayoutContext.Provider>
  );
}
